import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Banknote, Lock, Unlock, CalendarClock, MessageCircle } from "lucide-react";

export default function Payouts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [payouts, setPayouts] = useState([]);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const load = (m) => {
    api.get(`/admin/payouts?month=${m}`).then((r) => setPayouts(r.data));
    api.get("/admin/months").then((r) => setMonths(r.data));
  };
  useEffect(() => { load(month); }, [month]);

  const closed = months.find((m) => m.month === month && m.status === "closed");

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/payouts/generate", { month });
      setPayouts(data.payouts);
      toast.success(`Generated ${data.payouts.length} payouts for ${month}`);
      load(month);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const closeMonth = async () => {
    setClosing(true);
    try {
      const { data } = await api.post("/admin/months/close", { month });
      toast.success(`${month} closed · ${data.payouts.length} payouts · ${data.adjustments_proposed.length} overdue invoice(s) proposed for deposit adjustment`);
      setConfirmClose(false);
      load(month);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setClosing(false);
    }
  };
  const reopen = async () => {
    try {
      await api.post("/admin/months/reopen", { month });
      toast.success(`${month} reopened`);
      load(month);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const shareWhatsApp = async (p) => {
    try {
      const { data } = await api.post(`/admin/statements/${p.agent_id}/share`, { month, origin: window.location.origin });
      window.open(data.whatsapp_url, "_blank", "noopener");
      toast.success(`WhatsApp opened for ${p.agent_name}${data.phone ? "" : " (no phone on file - pick the contact)"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const markPaid = async (agent_id) => {
    await api.post("/admin/payouts/mark-paid", { month, agent_id });
    toast.success("Marked paid");
    load(month);
  };
  const markAllPaid = async () => {
    await api.post("/admin/payouts/mark-paid", { month });
    toast.success("All payouts marked paid");
    load(month);
  };

  const due = payouts[0]?.due_date;
  const anyOverdue = payouts.some((p) => p.overdue);

  return (
    <div>
      <PageHeader
        title="Payout Runs & Month Close"
        subtitle="Generate monthly payouts (commission + bonuses − returns − recoveries). Closing the month freezes its invoices and proposes deposit adjustments for overdue credit."
        actions={
          <div className="flex items-center gap-2">
            <Input type="month" className="w-40 h-10" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="payout-month-input" />
            {isAdmin && <Button variant="outline" onClick={generate} disabled={loading} data-testid="generate-payouts-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate"}
            </Button>}
            {isAdmin && (closed ? (
              <Button variant="outline" onClick={reopen} data-testid="reopen-month-button"><Unlock className="w-4 h-4 mr-1" /> Reopen</Button>
            ) : (
              <Button onClick={() => setConfirmClose(true)} data-testid="close-month-button"><Lock className="w-4 h-4 mr-1" /> Close month</Button>
            ))}
          </div>
        }
      />

      <div className="flex flex-wrap gap-3 mb-4">
        <Badge variant={closed ? "default" : "secondary"} className="gap-1 py-1" data-testid="month-status-badge">
          {closed ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />} {month} · {closed ? `closed ${closed.closed_at?.slice(0, 10)}` : "open"}
        </Badge>
        {due && (
          <Badge variant={anyOverdue ? "destructive" : "outline"} className="gap-1 py-1" data-testid="payout-due-badge">
            <CalendarClock className="w-3 h-3" /> Pay by {due}{anyOverdue && " · OVERDUE"}
          </Badge>
        )}
      </div>

      {payouts.length === 0 ? (
        <Empty>No payouts generated for {month}. Click Generate (or Close month).</Empty>
      ) : (
        <Card className="overflow-hidden">
          <div className="flex justify-between items-center px-4 py-3 border-b border-border">
            <span className="text-sm text-muted-foreground">{payouts.length} agents · base currency AED</span>
            <Button size="sm" variant="outline" onClick={markAllPaid} data-testid="mark-all-paid-button">
              <Banknote className="w-4 h-4 mr-1" /> Mark all paid
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-medium px-4 py-3">Agent</th>
                  <th className="text-right font-medium px-4 py-3">Collected</th>
                  <th className="text-right font-medium px-4 py-3">Base</th>
                  <th className="text-right font-medium px-4 py-3">Accel</th>
                  <th className="text-right font-medium px-4 py-3">Above-list</th>
                  <th className="text-right font-medium px-4 py-3">Bonuses</th>
                  <th className="text-right font-medium px-4 py-3">Deduct</th>
                  <th className="text-right font-medium px-4 py-3">Total (AED)</th>
                  <th className="text-right font-medium px-4 py-3">In currency</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.agent_id} className="border-b border-border/50 hover:bg-secondary/40" data-testid={`payout-row-${p.agent_id}`}>
                    <td className="px-4 py-3 font-medium">{p.agent_name}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{money(p.collected_sales)}</td>
                    <td className="px-4 py-3 text-right">{money(p.base_commission)}</td>
                    <td className="px-4 py-3 text-right">{money(p.accelerator_bonus)}</td>
                    <td className="px-4 py-3 text-right">{money(p.above_price_bonus)}</td>
                    <td className="px-4 py-3 text-right">{money(p.new_customer_bonus + p.star_bonus)}</td>
                    <td className="px-4 py-3 text-right text-destructive">{p.deductions + (p.recovery_from_commission || 0) ? `−${money(p.deductions + (p.recovery_from_commission || 0))}` : "—"}</td>
                    <td className="px-4 py-3 text-right font-display font-bold">{money(p.total)}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{money(p.total_local, p.currency)}</td>
                    <td className="px-4 py-3">
                      <Badge variant={p.status === "paid" ? "default" : p.overdue ? "destructive" : "secondary"}>{p.status === "paid" ? `paid ${p.payout_date?.slice(0, 10)}` : p.overdue ? "overdue" : p.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Link to={`/statement/${p.agent_id}?month=${month}`} className="text-xs text-primary hover:underline mr-2" data-testid={`statement-link-${p.agent_id}`}>Statement</Link>
                      <button onClick={() => shareWhatsApp(p)} className="inline-flex items-center gap-1 text-xs text-[#128C7E] hover:underline mr-2" data-testid={`whatsapp-share-${p.agent_id}`}><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</button>
                      {p.status !== "paid" && (
                        <Button size="sm" variant="ghost" onClick={() => markPaid(p.agent_id)} data-testid={`mark-paid-${p.agent_id}`}>Mark paid</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={confirmClose} onOpenChange={setConfirmClose}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Close {month}?</DialogTitle></DialogHeader>
          <ul className="text-sm text-muted-foreground space-y-1.5 list-disc pl-5">
            <li>Invoices dated in {month} become read-only (payments can still be recorded).</li>
            <li>Payouts are generated / refreshed for every agent (already-paid payouts are kept).</li>
            <li>Every unpaid invoice past its due date is <b>proposed</b> for deduction from the agent's deposit - you confirm each one on the Deposits page.</li>
          </ul>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmClose(false)}>Cancel</Button>
            <Button onClick={closeMonth} disabled={closing} data-testid="confirm-close-month-button">{closing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Close month"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
